"use client"

import type React from "react"

import { ChampignonsFormData } from "@repo/types"
import { Alert, AlertDescription } from "@repo/ui/components/ui/alert"
import { Button } from "@repo/ui/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/ui/components/ui/card"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@repo/ui/components/ui/form"
import { Input } from "@repo/ui/components/ui/input"; // Ensure this is the correct shadcn/ui Input
import { Label } from "@repo/ui/components/ui/label"
import { Progress } from "@repo/ui/components/ui/progress"
import { RadioGroup, RadioGroupItem } from "@repo/ui/components/ui/radio-group"
import { AlertCircle, ImageIcon, Upload } from "lucide-react"
import type { UseFormReturn } from "react-hook-form"
import { CHAMPIGNON_CATEGORY_OPTIONS } from "../utils/champignon-category"

interface ChampignonFormFieldsProps {
  form: UseFormReturn<ChampignonsFormData>
  error: string | null
  isEditing: boolean
  isSubmitting: boolean
  isSaving?: boolean
  isUploading?: boolean
  isOptimizingImage?: boolean
  uploadProgress?: number
  compressionMessage?: string | null
  canRetryUpload?: boolean
  previewUrl: string | null
  onCancel: () => void
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void | Promise<void>
  onSubmit: (data: ChampignonsFormData) => Promise<void>
  retryUpload?: () => Promise<void>
}

function getSubmitLabel({
  isEditing,
  isOptimizingImage,
  isSaving,
  isUploading,
  uploadProgress,
}: Pick<
  ChampignonFormFieldsProps,
  'isEditing' | 'isOptimizingImage' | 'isSaving' | 'isUploading' | 'uploadProgress'
>): string {
  if (isOptimizingImage) {
    return 'Optimisation de l\'image...';
  }
  if (isUploading) {
    return uploadProgress ? `Téléchargement... ${uploadProgress}%` : 'Téléchargement de l\'image...';
  }
  if (isSaving) {
    return 'Enregistrement...';
  }
  return isEditing ? 'Mettre à jour' : 'Créer le champignon';
}

export function ChampignonFormFields({
  form,
  error,
  isEditing,
  isSubmitting,
  isSaving = false,
  isUploading = false,
  isOptimizingImage = false,
  uploadProgress = 0,
  compressionMessage = null,
  canRetryUpload = false,
  previewUrl,
  onCancel,
  handleImageChange,
  onSubmit,
  retryUpload,
}: ChampignonFormFieldsProps) {
  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card className="shadow-lg border-0">
        <CardHeader className="space-y-2 pb-8">
          <CardTitle className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
            {isEditing ? "Modifier un champignon" : "Créer un nouveau champignon"}
          </CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            {isEditing
              ? "Modifiez les informations du champignon existant."
              : "Remplissez le formulaire pour créer un nouveau champignon."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {error && (
            <Alert variant="destructive" className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="space-y-3">
                <p>{error}</p>
                {canRetryUpload && retryUpload && (
                  <Button type="button" variant="outline" size="sm" onClick={() => void retryUpload()}>
                    Réessayer le téléchargement de l'image
                  </Button>
                )}
              </AlertDescription>
            </Alert>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column: Form Fields */}
                <div className="space-y-8">
                  {/* Name Field */}
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel className="text-base font-semibold text-gray-900">Nom du champignon *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Entrez le nom du champignon"
                            {...field}
                            value={field.value || ""}
                            className="h-12 text-base border-gray-200 focus:border-green-500 focus:ring-green-500/20"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* Category Field */}
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem className="space-y-4">
                        <FormLabel className="text-base font-semibold text-gray-900">Catégorie</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value || "F"}
                            className="grid grid-cols-1 sm:grid-cols-3 gap-4"
                          >
                            {CHAMPIGNON_CATEGORY_OPTIONS.map((option) => (
                              <div key={option.value} className="relative">
                                <RadioGroupItem
                                  value={option.value}
                                  id={`champignon-category-${option.value}`}
                                  className="peer sr-only"
                                />
                                <Label
                                  htmlFor={`champignon-category-${option.value}`}
                                  className="flex items-center justify-center rounded-lg border-2 border-gray-200 bg-white p-4 hover:bg-gray-50 peer-data-[state=checked]:border-green-500 peer-data-[state=checked]:bg-green-50 cursor-pointer transition-all duration-200"
                                >
                                  <div className="text-center">
                                    <div className="text-sm font-medium text-gray-900">
                                      {option.icon} {option.label}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                                  </div>
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* Attack Fields */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Types d'attaque</h3>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="attaque_f"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel className="text-sm font-medium text-gray-700">🍂 Attaque Feuillage</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Décrivez l'attaque sur le feuillage"
                                {...field}
                                value={field.value || ""}
                                className="h-11 border-gray-200 focus:border-green-500 focus:ring-green-500/20"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="attaque_r"
                        render={({ field }) => (
                          <FormItem className="space-y-3">
                            <FormLabel className="text-sm font-medium text-gray-700">🌱 Attaque Racines</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Décrivez l'attaque sur les racines"
                                {...field}
                                value={field.value || ""}
                                className="h-11 border-gray-200 focus:border-green-500 focus:ring-green-500/20"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* Right Column: Image Upload */}
                <div className="space-y-8">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                      Image du champignon
                    </h3>
                    <div className="space-y-4">
                      <div className="relative">
                        <Input id="image" type="file" accept="image/*" onChange={handleImageChange} className="sr-only" />
                        <Label
                          htmlFor="image"
                          className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                        >
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-8 h-8 mb-2 text-gray-400" />
                            <p className="text-sm text-gray-500">
                              <span className="font-semibold">Cliquez pour télécharger</span> ou glissez-déposez
                            </p>
                            <p className="text-xs text-gray-400">
                              PNG, JPG, WebP (max. 20 Mo, optimisation automatique)
                            </p>
                          </div>
                        </Label>
                      </div>
                      {isOptimizingImage && (
                        <p className="text-sm text-muted-foreground">Optimisation de l'image en cours...</p>
                      )}
                      {compressionMessage && !isOptimizingImage && (
                        <p className="text-sm text-green-700">{compressionMessage}</p>
                      )}
                      {isUploading && (
                        <div className="space-y-2">
                          <Progress value={uploadProgress} className="h-2" />
                          <p className="text-xs text-muted-foreground">
                            Téléchargement en cours ({uploadProgress}%)
                          </p>
                        </div>
                      )}
                      {previewUrl && (
                        <div className="relative">
                          <div className="rounded-lg border border-gray-200 overflow-hidden bg-white shadow-sm">
                            <div className="aspect-video relative">
                              <img
                                src={previewUrl || "/placeholder.svg"}
                                alt="Aperçu de l'image"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="p-3 bg-gray-50 border-t border-gray-100">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <ImageIcon className="w-4 h-4" />
                                <span>Aperçu de l'image sélectionnée</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-6 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  className="h-11 px-6 border-gray-300 hover:bg-gray-50 bg-transparent"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-11 px-6 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg disabled:opacity-50"
                >
                  {getSubmitLabel({
                    isEditing,
                    isOptimizingImage,
                    isSaving,
                    isUploading,
                    uploadProgress,
                  })}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
